import { AxiosError } from 'axios';
import i18n from '@/i18n';
import { del, get, post, put } from '@/utils';
import {
	IServerColumnConfig,
	IServerTableConfig
} from '@/components/table/column-config';
import {
	GEOM_TYPE_BY_ID,
	GEOM_TYPE_ID,
	IGeometryStyle,
	IGeometryStylePayload,
	IStilState,
	STATUS_ACTIVE_ID,
	STATUS_INACTIVE_ID
} from './types';

const BASE = 'api/geometry-styles';

type RawStyle = {
	id?: number | string;
	name?: string;
	status?: { id?: number; name?: string };
	geometryType?: { id?: number; name?: string };
	normalStyle?: IStilState;
	selectedStyle?: IStilState;
	createdBy?: { id?: number; firstName?: string; lastName?: string };
	createdByName?: string;
	createdAt?: string;
};

type StylesResponse = {
	items?: RawStyle[];
	totalPages?: number;
	totalCount?: number;
};

const buildQuery = (query: string, page: number, items: number): string => {
	const params = new URLSearchParams((query || '').replace(/^\?/, ''));
	params.set('PageNumber', String(page + 1));
	params.set('PageSize', String(items));
	return `?${params.toString()}`;
};

// Curăță cheile nule/undefined dintr-un obiect de stil (BE trimite doar cele relevante per tip).
const cleanStyle = (s?: IStilState | null): IStilState => {
	if (!s || typeof s !== 'object') return {};
	return Object.fromEntries(
		Object.entries(s).filter(([, v]) => v !== null && v !== undefined)
	) as IStilState;
};

const normalizeStyle = (raw: RawStyle, index: number): IGeometryStyle => ({
	id: raw.id ?? index,
	name: String(raw.name || ''),
	type: GEOM_TYPE_BY_ID[raw.geometryType?.id ?? 0] ?? 'arie',
	active: raw.status?.id === STATUS_ACTIVE_ID,
	statusName: raw.status?.name ?? undefined,
	default: cleanStyle(raw.normalStyle),
	selected: cleanStyle(raw.selectedStyle),
	createdByName:
		raw.createdByName ??
		`${raw.createdBy?.firstName ?? ''} ${raw.createdBy?.lastName ?? ''}`.trim() ??
		undefined,
	createdAt: raw.createdAt ?? undefined
});

const toPayload = (payload: IGeometryStylePayload): Record<string, unknown> => ({
	name: payload.name,
	geometryTypeId: GEOM_TYPE_ID[payload.type],
	statusId: payload.active ? STATUS_ACTIVE_ID : STATUS_INACTIVE_ID,
	normalStyle: cleanStyle(payload.default),
	selectedStyle: cleanStyle(payload.selected)
});

async function fetchStyles(
	query: string,
	page: number,
	items: number,
	onResponse?: (totalPages: number, totalCount: number) => void
): Promise<IGeometryStyle[]> {
	try {
		const res = await get(`${BASE}${buildQuery(query, page, items)}`);
		const data = res.data as StylesResponse | RawStyle[];
		if (Array.isArray(data)) {
			onResponse?.(1, data.length);
			return data.map(normalizeStyle);
		}
		const list = data.items ?? [];
		onResponse?.(data.totalPages || 1, data.totalCount || list.length);
		return list.map(normalizeStyle);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('geometryStyles.errorLoad'));
	}
}

async function fetchSingleStyle(styleId: number | string): Promise<IGeometryStyle> {
	try {
		const res = await get(`${BASE}/${styleId}`);
		return normalizeStyle(res.data as RawStyle, 0);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('geometryStyles.errorLoadOne'));
	}
}

async function postStyle(payload: IGeometryStylePayload): Promise<string | number> {
	try {
		const res = await post(BASE, toPayload(payload));
		return res?.status && res.status >= 200 && res.status < 300 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('geometryStyles.errorCreate'));
	}
}

async function putStyle(
	styleId: number | string,
	payload: IGeometryStylePayload
): Promise<string | number> {
	try {
		const res = await put(`${BASE}/${styleId}`, toPayload(payload));
		return res?.status && res.status >= 200 && res.status < 300 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('geometryStyles.errorUpdate'));
	}
}

async function deleteStyle(styleId: number | string): Promise<string | number> {
	try {
		const res = await del(`${BASE}/${styleId}`);
		return res?.status && res.status >= 200 && res.status < 300 ? res.status : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('geometryStyles.errorDelete'));
	}
}

// Stilurile ACTIVE (cel mult unul per tip) — endpoint anonim dedicat hărților.
// ARUNCĂ la eroare intenționat: apelantul (store-ul partajat din hook) distinge astfel o
// eroare (tranzitorie la cold-start — REîncearcă) de un răspuns reușit dar gol (niciun stil
// activ — fallback corect, fără retry). Harta nu se blochează niciodată: store-ul cade pe gol.
async function fetchActiveStyles(): Promise<IGeometryStyle[]> {
	const res = await get(`${BASE}/active`);
	const data = res.data as RawStyle[];
	return Array.isArray(data) ? data.map(normalizeStyle) : [];
}

// GET api/geometry-styles/table-config — configurația coloanelor tabelului Stiluri de
// geometrie (chei, etichete, ordine, vizibilitate/sortabilitate implicite). Se corelează
// în pagină cu definițiile locale prin applyServerColumnConfig.
async function fetchStylesTableConfig(): Promise<IServerColumnConfig[]> {
	try {
		const res = await get(`${BASE}/table-config`);
		const data = res.data as IServerTableConfig;
		return data?.columns ?? [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să încărcăm configurația tabelului.');
	}
}

export {
	fetchStyles,
	fetchStylesTableConfig,
	fetchSingleStyle,
	fetchActiveStyles,
	postStyle,
	putStyle,
	deleteStyle
};
